import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api.js';
import { trackMagicLinkRequested } from '../../lib/analytics.js';
import { Turnstile } from './Turnstile.js';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

interface Props {
  /** Visible submit button label. */
  submitLabel: string;
  /** Optional UTM/source string sent to the API. Useful to tell signup vs login apart in analytics. */
  intent?: 'login' | 'signup';
}

/**
 * Shared magic-link form used by both /auth/login and /auth/signup. The two
 * pages render different surrounding copy/CTAs but share this primitive so
 * the wire format and error handling stay identical.
 */
export function MagicLinkForm({ submitLabel, intent }: Props) {
  const { t, i18n } = useTranslation('auth');
  const [params] = useSearchParams();
  const next = params.get('next') ?? undefined;
  const [email, setEmail] = useState('');

  // CAPTCHA: solo activo si hay site key configurada. El token es de un solo
  // uso; tras un error re-montamos el widget (bump de `captchaKey`) para
  // obtener uno nuevo.
  const captchaEnabled = Boolean(TURNSTILE_SITE_KEY);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);

  const mutation = useMutation({
    mutationFn: async (e: string) =>
      api<{ ok: boolean }>('/auth/magic-link', {
        method: 'POST',
        body: {
          email: e,
          locale: i18n.language,
          intent,
          redirectTo: next ? new URL(next, window.location.origin).toString() : undefined,
          captchaToken: captchaToken ?? undefined,
        },
      }),
    onSuccess: () => {
      if (intent) trackMagicLinkRequested(intent);
    },
    onError: () => {
      // El token de Turnstile ya se consumió en el intento fallido; forzamos uno
      // nuevo para que el siguiente submit no reuse un token inválido.
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
    },
  });

  if (mutation.isSuccess) {
    return (
      <div className="card">
        <h2 className="text-xl font-semibold mb-2">{t('magic_link.sent_title')}</h2>
        <p
          className="text-neutral-400"
          dangerouslySetInnerHTML={{ __html: t('magic_link.sent_body', { email }) }}
        />
        <button
          type="button"
          onClick={() => mutation.reset()}
          className="text-sm text-[var(--color-accent)] hover:underline mt-4 inline-flex items-center gap-1"
        >
          ← {t('signup.email_label')}
        </button>
      </div>
    );
  }

  const captchaPending = captchaEnabled && !captchaToken;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (email && !captchaPending) mutation.mutate(email);
      }}
      className="space-y-4"
    >
      <label className="block">
        <span className="text-sm text-neutral-400">{t('signup.email_label')}</span>
        <input
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('signup.email_placeholder')}
          className="input mt-2"
        />
      </label>
      {captchaEnabled && TURNSTILE_SITE_KEY && (
        <Turnstile
          key={captchaKey}
          siteKey={TURNSTILE_SITE_KEY}
          theme="auto"
          language={i18n.language}
          onVerify={setCaptchaToken}
          onExpire={() => setCaptchaToken(null)}
        />
      )}
      <button
        type="submit"
        disabled={mutation.isPending || captchaPending}
        className="btn-primary w-full"
      >
        {mutation.isPending ? t('common:actions.loading') : submitLabel}
      </button>
      {mutation.isError &&
        (() => {
          const err = mutation.error as ApiClientError;
          const reason = err.details?.reason;
          const isCaptcha = typeof reason === 'string' && reason.startsWith('captcha');
          return (
            <p className="text-red-400 text-sm">{isCaptcha ? t('errors.captcha') : err.message}</p>
          );
        })()}
    </form>
  );
}
